<script lang="ts" setup>
import { fromNow } from '@thecompaniesapi/date-helpers'

const { modals, toggleModal } = useOverlays()
const { t } = useI18n()
const route = useRoute()
const { currentPlan } = useTeamsStore()

watch(
  () => route.name,
  () => toggleModal({
    opened: false,
    type: 'subscription',
  }),
)
</script>

<template>
  <UModal :ui="{ width: 'sm:max-w-5xl' }">
    <UCard
      :ui="{
        body: {
          base: 'grid max-h-[calc(60vh)] overflow-y-auto py-10',
          padding: 'p-6 md:p-8',
        },
        header: {
          base: 'p-6 md:p-8',
        },
      }"
    >
      <template #header>
        <BaseQuote
          character="power"
          class="mx-auto"
          size="sm"
        >
          <p>
            {{
              t(
                `titles.${modals.subscription.title || 'default'}`,
                {
                  maxScrollResults: numbro(currentPlan.restrictions.maxScrollResults),
                  totalCompanies: numbro(modals.subscription.data?.totalCompanies || 0),
                  used: modals.subscription.data?.rateLimitCount ? numbro(modals.subscription.data?.rateLimitCount) : undefined,
                  limit: modals.subscription.data?.rateLimitCount ? numbro(modals.subscription.data?.rateLimitCount) : undefined,
                  resetFromNow: modals.subscription.data?.rateLimitReset ? fromNow(modals.subscription.data?.rateLimitReset) : undefined,
                },
              )
            }}
          </p>

          <template v-if="modals.subscription?.data?.rateLimitCount">
            <p>
              {{ t('titles.tooManyRequestsRecap', { resetFromNow: modals.subscription.data?.rateLimitReset ? fromNow(modals.subscription.data?.rateLimitReset) : undefined }) }}
            </p>
          </template>

          <p v-if="modals.subscription.title">
            {{ t(`upgrade.${modals.subscription.title}`) }}
          </p>
        </BaseQuote>
      </template>

      <SubscriptionPlans
        class="mx-auto mb-10 max-w-6xl"
        :has-features="false"
        :has-powers="false"
      />
    </UCard>
  </UModal>
</template>

<i18n lang="json">
{
  "en": {
    "titles": {
      "default": "Create a subscription to access this feature.",
      "maxScrollResultsReached": "You've reached the limit of {maxScrollResults} viewable companies for your current plan.",
      "pageOneOnly": "You are limited to the first page of our {totalCompanies} companies matching your search criteria.",
      "signals": "You must have access to Signals to automatically get new companies matching your search criterias.",
      "tooManyRequests": "You have used {used} out of your {limit} max requests for this feature.",
      "tooManyRequestsRecap": "You will be able to make new requests to that endpoint {resetFromNow} from now."
    },
    "upgrade": {
      "maxScrollResultsReached": "Go beyond your current limits now by upgrading your subscription.",
      "pageOneOnly": "Go beyond the first page now by upgrading your subscription.",
      "signals": "Get access now by upgrading your subscription.",
      "tooManyRequests": "Increase your rate limits now by upgrading your subscription."
    }
  },
  "es": {
    "titles": {
      "default": "Crea una suscripción para acceder a esta función.",
      "maxScrollResultsReached": "Has alcanzado el límite de {maxScrollResults} empresas visibles para tu plan actual.",
      "pageOneOnly": "Eres limitado a la primera página de nuestros {totalCompanies} resultados que coinciden con tu búsqueda.",
      "signals": "Debes tener acceso a Signals para obtener automáticamente nuevas empresas que coincidan con tus criterios de segmentación.",
      "tooManyRequests": "Has hecho demasiadas solicitudes, actualiza tu suscripción para aumentar tus límites de solicitudes.",
      "tooManyRequestsRecap": "Podrás hacer nuevas solicitudes a ese endpoint {resetFromNow}."
    },
    "upgrade": {
      "maxScrollResultsReached": "Supera tus límites actuales ahora actualizando tu suscripción.",
      "pageOneOnly": "Ve más allá de la primera página ahora actualizando tu suscripción.",
      "signals": "Obtén acceso ahora actualizando tu suscripción.",
      "tooManyRequests": "Aumenta tus límites de solicitudes ahora actualizando tu suscripción."
    }
  },
  "fr": {
    "titles": {
      "default": "Créez une abonnement pour accéder à cette fonctionnalité.",
      "maxScrollResultsReached": "Vous avez atteint la limite de {maxScrollResults} sociétés visibles pour votre abonnement actuel.",
      "pageOneOnly": "Vous êtes limité à la première page de nos {totalCompanies} résultats qui correspondent à votre recherche.",
      "signals": "Vous devez avoir accès à Signals pour obtenir automatiquement de nouvelles sociétés qui correspondent à vos critères de segmentation.",
      "tooManyRequests": "Vous avez fait trop de requêtes, mettez à jour votre abonnement pour augmenter vos quotas de requêtes.",
      "tooManyRequestsRecap": "Vous pourrez faire de nouvelles requêtes à cet endpoint {resetFromNow}."
    },
    "upgrade": {
      "maxScrollResultsReached": "Dépassez vos limites actuelles maintenant en mettant à jour votre abonnement.",
      "pageOneOnly": "Allez au-delà de la première page maintenant en mettant à jour votre abonnement.",
      "signals": "Obtenez l'accès maintenant en mettant à jour votre abonnement.",
      "tooManyRequests": "Augmentez vos limites de requêtes maintenant en mettant à jour votre abonnement."
    }
  }
}
</i18n>
